import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import { Badge, Box, Button, Flex, Grid, Table } from "@mantine/core";
import { useModal } from "../contexts/useModal";
import { RoleAdd, RoleUpdate } from "../components/Modal";
import { toast } from "react-toastify";
import { IconPlus } from "@tabler/icons-react";
import { INT_MAX } from "../utils/util";
import { useAuth } from "../contexts/AuthContext";
import { fetchUserRoles } from "../utils/authRoles";
import { useCanWrite } from "../utils/useCanWrite";

const Permissions: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [roleList, setRoleList] = useState<
    { roles: string; roleName: string; workerYn: string }[]
  >([]);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const { showModal } = useModal();
  const { setRolesAction } = useAuth();
  useEffect(() => {
    getList();
  }, []);

  const getList = () => {
    axiosInstance
      .get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
        },
      })
      .then((res) => {
        setRoleList(res.data.data.list);
      })
      .catch((err) => {
        console.log(err);
        setRoleList([]);
      });
  };

  const refreshSessionRoles = async () => {
    try {
      const roles = await fetchUserRoles();
      if (roles.length > 0) {
        setRolesAction(roles);
      }
    } catch {
      /* ignore */
    }
  };

  const handleRoleUpdate = async (row: any) => {
    if (!canWrite) return;
    const flag = await showModal({
      component: RoleUpdate,
      props: { row: row },
    });
    if (flag) {
      getList();
      await refreshSessionRoles();
      toast.success("권한이 수정되었습니다. 좌측 메뉴가 갱신됩니다.");
    }
  };

  const handleRoleAdd = async () => {
    if (!canWrite) return;
    const flag = await showModal({ component: RoleAdd, props: {} });
    if (flag) {
      getList();
      await refreshSessionRoles();
      toast.success("권한이 등록되었습니다. 좌측 메뉴가 갱신됩니다.");
    }
  };

  const rows = roleList.map((row) => {
    const isSelected = selectedRow === row.roles;
    return (
      <Table.Tr
        key={row.roles}
        onClick={() => {
          setSelectedRow((prev) => (prev === row.roles ? null : row.roles));
          if (canWrite) {
            handleRoleUpdate(row);
          }
        }}
        style={{ cursor: canWrite ? "pointer" : "default" }}
        bg={isSelected ? "var(--mantine-color-blue-light)" : undefined}
        fw={isSelected ? "700" : undefined}
      >
        <Table.Td w={240} p={"4px 12px"}>
          {row.workerYn === "Y" && (
            <Badge color="blue" mr={"0.5rem"}>
              작업자
            </Badge>
          )}
          {row.roles}
        </Table.Td>
        <Table.Td p={"4px 12px"}>{row.roleName}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Flex bg={"#fafbfc"} bdrs={12} p={"0.75rem 1.5rem"} direction={"column"}>
      <Box fz={"1.75rem"} fw={700} pb={"1rem"}>
        {menuTitle}
      </Box>
      <Flex justify={"flex-end"} pb={"1rem"}>
        <Button
          leftSection={<IconPlus stroke={1.5} />}
          variant="gradient"
          gradient={{ from: "#e0eafc", to: "#b6e0fe", deg: 180 }}
          c={"#141414"}
          disabled={!canWrite}
          onClick={() => {
            handleRoleAdd();
          }}
        >
          등록
        </Button>
      </Flex>
      <Table>
        <Table.Thead>
          <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
            <Table.Th p={12} pos={"sticky"} top={0}>
              권한ID
            </Table.Th>
            <Table.Th p={12} pos={"sticky"} top={0}>
              이름
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </Flex>
  );
};

export default Permissions;
